import { WidgetId } from 'types/idTypes';
import { WidgetType } from 'types/widget.types';
import {
  getNextResponsiveWidgetMember,
  getPreviousResponsiveWidgetMember,
  isLastResponsiveMember,
  isResponsiveWidgetBase,
} from 'widgets/ResponsiveWidgets/ResponsiveWidget.helpers';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { WidgetMap } from 'widgets/Widget.types';

import { ActiveWidget } from './widgetControlSlice';

const getNextGroupMember = (activeWidgets: ActiveWidget[], widgets: WidgetMap, activeId: WidgetId) => {
  const { groupMembers } = activeWidgets[0];
  const activeIdx = groupMembers.findIndex((wId: WidgetId) => wId === activeId);
  const nextIdx = activeIdx + 1;
  const nextId = groupMembers[nextIdx] ? groupMembers[nextIdx] : groupMembers[0];

  // if next group member is a responsive widget - selected the first member of the responsive widget
  if (isResponsiveWidgetBase(nextId)) {
    return getNextResponsiveWidgetMember(nextId, widgets, nextId);
  }

  return nextId;
};

const getPreviousGroupMember = (activeWidgets: ActiveWidget[], widgets: WidgetMap, activeId: WidgetId) => {
  const { groupMembers } = activeWidgets[0];
  const activeIdx = groupMembers.findIndex((wId: WidgetId) => wId === activeId);
  const nextIdx = activeIdx - 1;
  const nextId = groupMembers[nextIdx] ? groupMembers[nextIdx] : groupMembers[groupMembers.length - 1];

  // if next group member is a responsive widget - selected the first member of the responsive widget
  if (isResponsiveWidgetBase(nextId)) {
    return getNextResponsiveWidgetMember(nextId, widgets, nextId);
  }

  return nextId;
};

export const getNextActiveWidget = (
  widgetOrder: WidgetId[],
  activeWidgets: ActiveWidget[],
  widgets: WidgetMap,
): WidgetId | undefined => {
  if (!widgetOrder.length) return;
  if (activeWidgets.length > 1) return;
  if (activeWidgets.length === 0) return widgetOrder[0];

  const responsiveGroupId = activeWidgets[0].responsiveGroupId;
  const groupId = activeWidgets[0].groupId;
  const activeId = activeWidgets[0].id;

  // get the next responsive widget member
  const isResponsiveWidgetMember = responsiveGroupId && activeId !== responsiveGroupId;
  if (isResponsiveWidgetMember) {
    const id = getNextResponsiveWidgetMember(responsiveGroupId, widgets, activeId);

    // For responsive widgets move on to the next non-responsive widget if its the last member
    // (unlike groups where we loop back to the first member)
    const isLastMember = isLastResponsiveMember(widgets, activeId, responsiveGroupId);
    if (isLastMember) {
      if (groupId) return getNextGroupMember(activeWidgets, widgets, responsiveGroupId);

      // get the next widget in the widget order after the responsive group
      const activeIdx = widgetOrder.findIndex((wId: WidgetId) => wId === responsiveGroupId);
      const nextIdx = activeIdx + 1;

      if (widgetOrder[nextIdx]) return widgetOrder[nextIdx];
      return widgetOrder[0];
    }

    return id;
  }

  // get the next group widget member
  const isResponsiveWidgetBase = responsiveGroupId === activeId;
  const isResponsiveWidgetInGroup = activeId === responsiveGroupId && !!groupId;
  const isGroupMember =
    activeWidgets.length === 1 &&
    !!activeWidgets[0].groupMembers.length &&
    getWidgetTypeFromId(activeId) !== WidgetType.Group &&
    (!isResponsiveWidgetBase || isResponsiveWidgetInGroup);

  if (isGroupMember) return getNextGroupMember(activeWidgets, widgets, activeId);

  // get the next widget in the widget order
  const activeIdx = widgetOrder.findIndex((wId: WidgetId) => wId === activeId);
  const nextIdx = activeIdx + 1;

  if (widgetOrder[nextIdx]) return widgetOrder[nextIdx];
  return widgetOrder[0];
};

export const getPreviousActiveWidget = (
  widgetList: WidgetId[],
  activeWidgets: ActiveWidget[],
  widgets: WidgetMap,
): WidgetId | undefined => {
  if (!widgetList.length) return;
  if (activeWidgets.length > 1) return;
  if (activeWidgets.length === 0) return widgetList[0];

  const responsiveGroupId = activeWidgets[0].responsiveGroupId;
  const groupId = activeWidgets[0].groupId;
  const activeId = activeWidgets[0].id;

  // get the next responsive widget member
  const isResponsiveWidgetMember = responsiveGroupId && activeId !== responsiveGroupId;
  if (isResponsiveWidgetMember) {
    const id = getPreviousResponsiveWidgetMember(responsiveGroupId, widgets, activeId);

    // For responsive widgets move on to the next non-responsive widget if its the last member
    // (unlike groups where we loop back to the first member)
    const isFirstMember = isLastResponsiveMember(widgets, activeId, responsiveGroupId);
    if (isFirstMember) {
      if (groupId) return getPreviousGroupMember(activeWidgets, widgets, responsiveGroupId);

      // get the previous widget in the widget order
      const activeIdx = widgetList.findIndex((wId: WidgetId) => wId === responsiveGroupId);
      const nextIdx = activeIdx - 1;

      if (widgetList[nextIdx]) return widgetList[nextIdx];
      return widgetList[widgetList.length - 1];
    }

    return id;
  }

  // get the previous group widget member
  const isResponsiveWidgetBase = responsiveGroupId === activeId;
  const isResponsiveWidgetInGroup = activeId === responsiveGroupId && !!groupId;
  const isGroupMember =
    activeWidgets.length === 1 &&
    !!activeWidgets[0].groupMembers.length &&
    getWidgetTypeFromId(activeId) !== WidgetType.Group &&
    (!isResponsiveWidgetBase || isResponsiveWidgetInGroup);
  if (isGroupMember) return getPreviousGroupMember(activeWidgets, widgets, activeId);

  // get the previous widget in the widget order
  const activeIdx = widgetList.findIndex((wId: WidgetId) => wId === activeId);
  const nextIdx = activeIdx - 1;

  if (widgetList[nextIdx]) return widgetList[nextIdx];
  return widgetList[widgetList.length - 1];
};
